import * as React from "react";
import { MinusCircleOutlined } from "@ant-design/icons";
import { formSetValue, useCpFormValue } from "../../../gvc/testNewForm/utils";
import { cloneDeep } from "lodash";
import { CpSelect } from "../../../gvc/testNewForm/CpSelect";
import { CpInput } from "../../../gvc/testNewForm/CpInput";
import { CpInputNumber } from "../../../gvc/testNewForm/CpInputNumber";
import { CpKindSelect } from "../../../gvc/testNewForm/temp/CpKindSelect";
import { parseLink } from "../../../../utils/linkParser/linkParser";
import { useCpFormContext } from "../../../../components/form/useCpFormContext";

interface Props {
  portIndex: number;
  routeId: string;
}
export const PortRoute: React.FC<Props> = ({ portIndex, routeId }) => {
  const { id: formId } = useCpFormContext();
  const gvcLink = useCpFormValue("gvc.link");
  const gvcName = React.useMemo(() => {
    const parsedLink = parseLink(gvcLink, { useInputCtx: true });
    return parsedLink.name;
  }, [gvcLink]);
  const routingMode = useCpFormValue("advanced.spec.routingMode");
  const acceptAll = useCpFormValue("advanced.spec.acceptAll");
  const routes = useCpFormValue(`advanced.spec.ports.${portIndex}.routes`);

  const routeIndex = React.useMemo(() => {
    return routes.findIndex((r) => r.id === routeId);
  }, [portIndex, routeId, routes]);

  const prefixType = useCpFormValue(`advanced.spec.ports.${portIndex}.routes.${routeIndex}.prefixType`) || "prefix";
  const hostType = useCpFormValue(`advanced.spec.ports.${portIndex}.routes.${routeIndex}.hostType`) || "prefix";

  function handleRemoveRoute(routeIdToRemove: string) {
    const _routes = cloneDeep(routes);
    const routeIndexToRemove = routes.findIndex((r) => r.id === routeIdToRemove);
    if (routeIndexToRemove >= 0) {
      _routes.splice(routeIndexToRemove, 1);
      formSetValue(formId, `advanced.spec.ports.${portIndex}.routes`, _routes);
    }
  }

  // 1 type - all
  // 2 prefix/regex - all
  // 2 replacePrefix - all
  // 1 type (only when either acceptAllHosts or acceptAllSubdomains are enabled)
  // 2 hostPrefix/hostRegex (only when either acceptAllHosts or acceptAllSubdomains are enabled)
  // 2 workloadLink - pathBased
  // 1 port - all
  // 1 replica - workloadSubdomain

  const colCount = React.useMemo(() => {
    let val = 0;
    if (routingMode === "pathBased") {
      val = 8;
    }
    if (routingMode === "workloadSubdomain") {
      val = 7;
    }
    if (acceptAll !== "none") {
      val += 3;
    }
    return val;
  }, [routingMode, acceptAll]);

  return (
    <div key={`port-routes-${portIndex}-route-${routeId}`}>
      <div className="mb-1">
        <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${colCount}, minmax(0, 1fr))` }}>
          <CpSelect
            name={`advanced.spec.ports.${portIndex}.routes.${routeIndex}.prefixType`}
            className={"col-span-1"}
            options={[
              { label: "Prefix", value: "prefix" },
              { label: "Regex", value: "regex" },
            ]}
          />
          {prefixType === "prefix" ? (
            <CpInput name={`advanced.spec.ports.${portIndex}.routes.${routeIndex}.prefix`} className={"col-span-2"} />
          ) : (
            <CpInput name={`advanced.spec.ports.${portIndex}.routes.${routeIndex}.regex`} className={"col-span-2"} />
          )}
          <CpInput
            name={`advanced.spec.ports.${portIndex}.routes.${routeIndex}.replacePrefix`}
            className={"col-span-2"}
          />
          {acceptAll !== "none" ? (
            <>
              <CpSelect
                name={`advanced.spec.ports.${portIndex}.routes.${routeIndex}.hostType`}
                className={"col-span-1"}
                options={[
                  { label: "Prefix", value: "prefix" },
                  { label: "Regex", value: "regex" },
                ]}
              />
              {hostType === "prefix" ? (
                <CpInput
                  className={"col-span-2"}
                  name={`advanced.spec.ports.${portIndex}.routes.${routeIndex}.hostPrefix`}
                />
              ) : (
                <CpInput
                  name={`advanced.spec.ports.${portIndex}.routes.${routeIndex}.hostRegex`}
                  className={"col-span-2"}
                />
              )}
            </>
          ) : null}
          {routingMode === "pathBased" ? (
            <CpKindSelect
              name={`advanced.spec.ports.${portIndex}.routes.${routeIndex}.workload`}
              optionValue="name"
              key={gvcName}
              // TODO fix
              // onChangeExtra={(value, item) => {
              //   const selfLink = linksOf(item).self!;
              //   setValue(`advanced.spec.ports.${portIndex}.routes.${routeIndex}.workload.link`, selfLink);
              //   setValue(`advanced.spec.ports.${portIndex}.routes.${routeIndex}.workload.item`, item);
              // }}
              className={"col-span-2"}
              kind="workload"
              queries={[{ rel: "gvc", value: gvcName }]}
            />
          ) : null}
          {routingMode === "workloadSubdomain" ? (
            <>
              <CpInputNumber
                name={`advanced.spec.ports.${portIndex}.routes.${routeIndex}.port`}
                className={"free-width w-full col-span-1"}
              />
              <div className="col-span-1 flex items-start gap-2">
                <CpInputNumber
                  className="free-width w-full"
                  name={`advanced.spec.ports.${portIndex}.routes.${routeIndex}.replica`}
                />
                <MinusCircleOutlined
                  style={{ marginTop: 6 }}
                  className="color-danger"
                  onClick={() => handleRemoveRoute(routeId)}
                />
              </div>
            </>
          ) : (
            <div className="col-span-1 flex items-start gap-2">
              <CpInputNumber
                className="free-width w-full "
                name={`advanced.spec.ports.${portIndex}.routes.${routeIndex}.port`}
              />
              <MinusCircleOutlined
                style={{ marginTop: 6 }}
                className="color-danger"
                onClick={() => handleRemoveRoute(routeId)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
